import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { INVOICES_TAB } from '../../../../constants/navigation';
import {
  BILLING_VARIABLES,
  PREVIEW_CONTENT_TYPE,
} from '../../../../constants/textEditor';
import { AppContext } from '../../../../contexts/AppContext';
import { NotificationService } from '../../../../services/NotificationService';
import Paragraph from '../../../atoms/paragraphs/Paragraph';
import SelectWithValues from '../../../atoms/selects/SelectWithValues';
import Span from '../../../atoms/spans/Span';
import Alert from '../../../molecules/alerts/Alert';
import RequestHandelingAlert from '../../../organisms/alerts/RequestHandelingAlert';
import SearchByClient from '../../../organisms/dropdowns/SearchByClient';
import TextEditor from '../../../organisms/editor/TextEditor';
import EditorPreview from '../../../organisms/editor/previews/EditorPreview';
import SuccessModal from '../../feedback/modal/SuccessModal';
import { GlobalsHelpers } from '../../helper/globals';
import { fetchClients } from '../../invoices/util/api/request';
import { BillingAlertsHelper } from './helper';
import Menu, { MENU_CATEGORY } from '../../../organisms/menu/Menu';
import Button from '../../../atoms/buttons/Button';
import NotAllowedLayout from '../../feedback/not-allowed/NotAllowedLayout';
import CheckBoxLabel from '../../../molecules/labels/CheckBoxLabel';

const BillingAlertsLayout = (props) => {
  const { appState } = useContext(AppContext);
  const defaultClient = BillingAlertsHelper.getDefaultClientOption(props.intl);
  const defaultFormData = BillingAlertsHelper.getInitialState(props.intl);

  const emailTemplate =
    props.intl.messages['billingTemplateContentEmail'] +
    props.intl.messages['billingTemplateFixedEmail'];

  const [form, setForm] = useState(defaultFormData);
  const [byDefaultClientForm, setByDefaultClientForm] =
    useState(defaultFormData);
  const [previousClientForm, setPreviousClientForm] = useState({});
  const [clientSelected, setClientSelected] = useState(defaultClient);
  const [clientList, setClientList] = useState(defaultClient);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [requestErrors, setRequestErrors] = useState('');
  const [hasTemplate, setHasTemplate] = useState(false);
  const [applyDefaultSingle, setApplyDefaultSingle] = useState(false);
  const [applyDefaultAll, setApplyDefaultAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * Update form values
   *
   * @param {string} name - column name
   * @param {string} value
   */
  const handleFields = (name, value) => {
    setForm({
      ...form,
      [name]: value,
    });
  };

  /**
   * Verify if field should be disabled
   *
   * @returns {boolean}
   */
  const shouldFieldBeDisabled = () => {
    return (
      form?.frequency === GlobalsHelpers.FREQUENCY_TYPE.NOT_SEND ||
      applyDefaultSingle
    );
  };

  /**
   * Mount header
   * @param {string} key - key to translation
   */
  const mountHeaderOfSection = (key) => {
    return (
      <div className='column px-0 mt-5'>
        <Paragraph className='text-header h4 mb-0'>
          {props.intl.messages[`${key}Title`]}
        </Paragraph>
        <div className='sep-light mt-1' />
      </div>
    );
  };

  /**
   * Mount editor
   * @param {string} type - type of preview (sms/email)
   */
  const mountTextEditorWithPreview = (type) => {
    const previewText = form?.email_template
      ? form?.email_template + props.intl.messages['billingTemplateFixedEmail']
      : form?.email_template || emailTemplate;

    return (
      <div key={type} className='row px-0 mx-0 mt-4'>
        <div className='col-md-6 ps-0'>
          <TextEditor
            defaultValue={
              form?.email_template ||
              props.intl.messages['billingTemplateContentEmail']
            }
            onChange={(value) => {
              handleFields('email_template', value);
            }}
            variables={BILLING_VARIABLES}
            disabled={applyDefaultSingle}
          />
        </div>
        <div className='col-md-6 pe-0'>
          <EditorPreview
            type={PREVIEW_CONTENT_TYPE[type.toUpperCase()]}
            template={previewText}
            accountLogo={props.template?.email?.logo_url || ''}
          />
          <Paragraph>
            {props.intl.messages['templateVariableMessage']}
          </Paragraph>
        </div>
      </div>
    );
  };

  const onSubmit = async () => {
    setIsSaveDisabled(true);

    const response = hasTemplate
      ? await NotificationService.updateBillingAlertConfiguration(form)
      : await NotificationService.createBillingAlertConfiguration(form);

    if (!response.error) {
      setIsSubmitted(true);
      setRequestErrors('');
      setIsSaveDisabled(false);
      resetState(clientSelected);
      return;
    } else {
      setRequestErrors(response?.error);
    }
  };

  /**
   * Set the selected client object
   * @param {string} id - client_id
   */
  const handleDropdownSelection = (id, reset = false) => {
    if (reset) {
      searchClientByTerm('...');
    }

    const selected = clientList.filter((client) => client.id === id);
    handleFields('client_id', id);
    setClientSelected(selected);
  };

  /**
   * Fetch a list of client by name
   * @param {string} search - client name
   */
  const searchClientByTerm = (search) => {
    setSearchTerm(search);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchTerm.length >= 3 || searchTerm === '') {
        const response = await fetchClients(
          appState.accountId,
          searchTerm === '...' ? '' : searchTerm,
          INVOICES_TAB
        );

        if (searchTerm === '...') {
          setSearchTerm('');
        }

        if (!response.error) {
          setClientList([...defaultClient, ...response.clients]);
        }
      } else {
      }
    }, 500); // Wait 500ms after the last keystroke

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line
  }, [searchTerm]);

  /**
   * Reset state
   * @param {object} selectedClient - client selected
   * @returns {boolean}
   */
  const resetState = (selectedClient) => {
    setClientSelected(selectedClient);
    searchClientByTerm('...');
  };

  const onChangeApplyDefaultSingle = () => {
    if (applyDefaultSingle) {
      setForm({
        ...previousClientForm,
        client_id: form?.client_id,
        replicate: !applyDefaultSingle,
      });
    } else {
      setForm({
        ...byDefaultClientForm,
        client_id: form?.client_id,
        replicate: !applyDefaultSingle,
      });
    }

    setApplyDefaultSingle(!applyDefaultSingle);
  };

  const onChangeApplyDefaultAll = () => {
    setForm({ ...form, replicate: !applyDefaultAll });
    setApplyDefaultAll(!applyDefaultAll);
  };

  useEffect(() => {
    const fetchConfiguration = async () => {
      const response = await NotificationService.getBillingAlertConfiguration(
        form?.client_id
      );

      if (form?.client_id === '') {
        setByDefaultClientForm({
          ...response.billing_alert,
          client_has_email: defaultFormData.client_has_email,
          client_id: form?.client_id,
          frequency:
            response.billing_alert?.frequency || defaultFormData.frequency,
          due: response.billing_alert?.due || defaultFormData.due,
          weekday: response.billing_alert?.weekday || defaultFormData.weekday,
          period: response.billing_alert?.period || defaultFormData.period,
        });

        setApplyDefaultAll(false);
        setApplyDefaultSingle(false);
        setPreviousClientForm({});
      } else {
        setApplyDefaultAll(false);
        setPreviousClientForm({});

        if (response.billing_alert?.configurable === undefined) {
          setApplyDefaultSingle(true);
        } else {
          setApplyDefaultSingle(false);
        }
      }

      if (!response.error && response.billing_alert) {
        if (response.billing_alert?.configurable === undefined) {
          setForm({
            ...byDefaultClientForm,
            client_id: form?.client_id,
            email_template: byDefaultClientForm.email_template,
          });
        } else {
          setForm({
            ...response.billing_alert,
            client_id: form?.client_id,
            frequency:
              response.billing_alert?.frequency || defaultFormData.frequency,
            due: response.billing_alert?.due || defaultFormData.due,
            weekday: response.billing_alert?.weekday || defaultFormData.weekday,
            period: response.billing_alert?.period || defaultFormData.period,
            email_template:
              response.billing_alert?.email_template ||
              byDefaultClientForm.email_template ||
              defaultFormData.email_template,
          });
        }

        setPreviousClientForm({
          ...response.billing_alert,
          client_id: form?.client_id,
          frequency:
            response.billing_alert?.frequency || defaultFormData.frequency,
          due: response.billing_alert?.due || defaultFormData.due,
          weekday: response.billing_alert?.weekday || defaultFormData.weekday,
          period: response.billing_alert?.period || defaultFormData.period,
          email_template:
            response.billing_alert?.email_template ||
            defaultFormData.email_template,
        });

        setHasTemplate(response.billing_alert?.frequency ? true : false);
        return;
      }
      setForm({ ...defaultFormData, client_id: form?.client_id });
      setHasTemplate(false);
    };

    fetchConfiguration();
    searchClientByTerm('...');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.client_id]);

  return (
    <div
      id='main-content'
      className='container --billing-alerts position-relative'
    >
      {appState.PlanHasBillingAlerts !== 'true' && (
        <NotAllowedLayout
          title='notAllowedTitle'
          description='notAllowedDescription'
        />
      )}

      <div className='row px-0 mx-0 justify-content-center'>
        <div className='col-11 col-lg-9 ps-0'>
          <div className='container px-0 mx-0'>
            <div className='row px-0 mx-0 content-block advanced-search-block'>
              <div className='row px-0 mx-0 title-block'>
                <div className='col-12 px-0'>
                  <Paragraph
                    id='billing-alerts-title'
                    className='text-header h2'
                  >
                    {props.intl.messages['billingAlertsTitle']}
                  </Paragraph>

                  <Paragraph className='text-paragraph mt-3 mb-2'>
                    <Span className='d-block'>
                      {props.intl.messages['billingAlertsDescription']}
                    </Span>
                  </Paragraph>

                  {requestErrors.length > 0 && (
                    <RequestHandelingAlert error={requestErrors} />
                  )}
                </div>
              </div>
            </div>

            <div className='form-container'>
              <div className='row px-0 mx-0'>
                {!form.client_has_email && (
                  <div className='col-12 px-0 mb-4'>
                    <Alert
                      id='regularization-information-message'
                      alertType={`alert-warning --icon mt-3`}
                      iconClassName='fas fa-circle-exclamation'
                    >
                      <FormattedMessage
                        id={'userHasNoEmail'}
                        key={'client-alert'}
                      />
                    </Alert>
                  </div>
                )}

                <div className='position-relative px-0 col-12 col-md-6'>
                  <SearchByClient
                    withoutCheckbox
                    clientSearchTerm={searchTerm}
                    clients={clientList}
                    clientsToFilterDocs={[clientSelected[0]?.id]}
                    clientNameSelected={[clientSelected[0]?.name]}
                    getClientsMatch={searchClientByTerm}
                    onSelectRadio={handleDropdownSelection}
                    onResetFilter={handleDropdownSelection}
                  />
                </div>

                {form?.client_id === '' && (
                  <CheckBoxLabel
                    id='billingAlertsApplyDefaultAll'
                    className='checkbox mt-4 mb-0'
                    onChange={onChangeApplyDefaultAll}
                    checked={applyDefaultAll}
                  >
                    <span className='ms-2'>
                      {props.intl.messages['billingAlertsApplyDefaultAll']}
                    </span>
                  </CheckBoxLabel>
                )}

                {form?.client_id !== '' && (
                  <CheckBoxLabel
                    id='billingAlertsApplyDefaultSingle'
                    className='checkbox mt-4 mb-0'
                    onChange={onChangeApplyDefaultSingle}
                    checked={applyDefaultSingle}
                  >
                    <span className='ms-2'>
                      {props.intl.messages['billingAlertsApplyDefaultSingle']}
                    </span>
                  </CheckBoxLabel>
                )}
              </div>

              {/* Starts frequency section */}
              {mountHeaderOfSection('billingFrequencySection')}
              <div className='row px-0 mx-0 mt-4'>
                <div className='col-lg-6 col-12 px-0 pe-lg-2'>
                  <label className='text-label mb-2'>
                    {props.intl.messages['billingFrequencyLabel']}
                  </label>
                  <SelectWithValues
                    id='frequency'
                    key='frequency'
                    valueName='value'
                    options={GlobalsHelpers.getFrequencyOfAlertList(props.intl)}
                    value={form?.frequency}
                    onChange={(e) => handleFields('frequency', e.target.value)}
                    disabled={applyDefaultSingle}
                  />
                </div>

                <div className='col-lg-6 col-12 px-0 ps-lg-2'>
                  <label className='text-label mb-2'>
                    {props.intl.messages['billingFrequencyWeekDayLabel']}
                  </label>
                  <SelectWithValues
                    id='weekday'
                    key='weekday'
                    valueName='value'
                    options={GlobalsHelpers.getDaysOfWeekList(props.intl)}
                    value={form?.weekday}
                    onChange={(e) => handleFields('weekday', e.target.value)}
                    disabled={shouldFieldBeDisabled()}
                  />
                </div>
              </div>

              <div className='row px-0 mx-0 mt-4'>
                <div className='col-lg-6 col-12 px-0 pe-lg-2'>
                  <label className='text-label mb-2'>
                    {props.intl.messages['billingFrequencyStartLabel']}
                  </label>
                  <SelectWithValues
                    id='before_overdue'
                    key='due'
                    valueName='value'
                    options={GlobalsHelpers.getTypeOfFrequencyList(props.intl)}
                    value={form.due}
                    onChange={(e) => handleFields('due', e.target.value)}
                    disabled={shouldFieldBeDisabled()}
                  />
                </div>

                <div className='col-lg-6 col-12 px-0 ps-lg-2'>
                  <label className='text-label mb-2'>
                    {props.intl.messages['billingPeriodLabel']}
                  </label>
                  <SelectWithValues
                    id='period'
                    key='period'
                    valueName='value'
                    options={GlobalsHelpers.getWorkPeriodList(props.intl)}
                    value={form?.period}
                    onChange={(e) => handleFields('period', e.target.value)}
                    disabled={shouldFieldBeDisabled()}
                  />
                </div>
              </div>

              {/* Start email template section */}
              <div className='row px-0 mx-0'>
                {mountHeaderOfSection('billingEmailTemplateSection')}

                {mountTextEditorWithPreview('email_alerts')}
              </div>

              {/* Start sms template section */}
              {/* <div className='row mt-3'>
                {mountHeaderOfSection('billingSMSTemplateSection')}

                <div className='col-md-12 mt-2'>
                  <CheckBoxLabel
                    id='sms'
                    className='checkbox'
                    value={!form?.sms}
                    onChange={(e) => handleFields('sms', !e.target.checked)}
                    checked={form?.sms ? false : true}
                    disabled={shouldFieldBeDisabled()}
                  >
                    {props.intl.messages['billingAlertNotSendSMS']}
                  </CheckBoxLabel>
                </div>
                {mountTextEditorWithPreview('sms')}
              </div> */}
            </div>
            <div className='d-flex flex-column justify-content-end'>
              <Button
                className='button button-primary ms-auto'
                id={'save_button'}
                label={props.intl.messages['saveForm']}
                onClick={onSubmit}
                shouldRenderAlert={isSubmitted}
                success={isSubmitted}
                disabled={isSaveDisabled || !form.client_has_email}
              />
            </div>
          </div>
        </div>

        <div className='col-lg-3 mt-5'>
          <Menu {...props} activeCategory={MENU_CATEGORY.PERSONALIZATION} />
        </div>
      </div>

      {isSubmitted && requestErrors === '' && (
        <SuccessModal
          messageKey='dataSavedSuccess'
          onClose={() => setIsSubmitted(false)}
        />
      )}
    </div>
  );
};

export default injectIntl(BillingAlertsLayout);
